// Standard New Build reservation terms + booking link.
//
// New Build reservations do not take a Stripe payment (unlike off-market property
// reservations). Instead the buyer accepts these standard terms and books a call with
// the team via Calendly. The terms are fixed for every development and every site.

export const NEW_BUILD_RESERVATION_CALENDLY_URL =
  "https://calendly.com/michael-mypropertygroup/30min";

export interface NewBuildReservationFee {
  label: string;
  value: string;
  note: string;
}

export interface NewBuildReservationSection {
  heading: string;
  items: string[];
}

// Booking URL with attribution params, so the central Calendly funnel can still tell
// which partner site and development a reservation came from (one shared base link).
export function buildNewBuildReservationBookingUrl(
  siteName: string,
  developmentName: string
): string {
  const url = new URL(NEW_BUILD_RESERVATION_CALENDLY_URL);
  url.searchParams.set("utm_source", siteName || "whitelabel");
  url.searchParams.set("utm_medium", "reserve_modal");
  url.searchParams.set("utm_campaign", "new_build_reservation");
  if (developmentName) {
    url.searchParams.set("utm_content", developmentName);
  }
  return url.toString();
}

// A development is reservable unless it tracks units and none remain available.
// When units are not tracked at all (totalUnits === 0) the development is still
// reservable — only an explicitly sold/held-out unit list blocks reservation.
export function isNewBuildReservable(
  totalUnits: number,
  availableUnitsCount: number
): boolean {
  if (totalUnits <= 0) return true;
  return availableUnitsCount > 0;
}

// Login redirect that returns the user to this development after authenticating.
// Path is the singular `/new-build/:id` detail route (the `/new-builds/:id` plural
// form is a 404 — this guards the redirect against that earlier regression).
export function newBuildLoginRedirectPath(id: string): string {
  return `/login?redirectTo=/new-build/${id}`;
}

// Headline figures, shown as highlighted summary boxes at the top of the modal.
export const NEW_BUILD_RESERVATION_FEES: NewBuildReservationFee[] = [
  { label: "Reservation Fee", value: "£2,500", note: "Non-refundable" },
  { label: "Admin Fee", value: "£500", note: "Non-refundable" },
  { label: "Deposit", value: "20%", note: "Within 28 days (less reservation fee)" },
  { label: "Completion Balance", value: "80%", note: "On legal completion" },
];

// Full standard terms, grouped into titled sections for readability.
export const NEW_BUILD_RESERVATION_SECTIONS: NewBuildReservationSection[] = [
  {
    heading: "Reservation Period & Exchange",
    items: [
      'This Reservation Agreement will end 28 days after the date of receipt of the contract documents by the Buyer’s solicitor (the "Expiry Date").',
      "Exchange of contracts is due within 28 calendar days from receipt of contract documents by the Buyer’s Solicitor.",
      "The Seller agrees not to sell or attempt to sell the Property unless the Expiry Date has passed.",
    ],
  },
  {
    heading: "Fees & Payment",
    items: [
      "A non-refundable Reservation Fee of £2,500.",
      "A non-refundable Admin fee of £500.",
      "20% deposit sum is payable in 28 days (minus the £2,500 reservation fee).",
      "£500 fee is chargeable as an admin fee, covering AML costs and other banking and administrative costs of sale. Completion monies — balance of 80% of the purchase price (taking into account the reservation fee paid together with any other sums due under the contract) is payable upon legal completion of the purchase of the property.",
    ],
  },
  {
    heading: "Cancellation",
    items: [
      "If the Buyer cancels the Reservation or fails to exchange before the Expiry Date then the Reservation Fee is non-refundable.",
    ],
  },
  {
    heading: "Identity Verification (Anti-Money Laundering)",
    items: [
      "In line with The Money Laundering, Terrorist Financing and Transfer of Funds (Information on the Payer) Regulations 2017, as a regulated profession, we are duty bound to carry out due diligence on all of our clients to confirm their identity. Rather than traditional methods in which you would have to produce multiple utility bills and a photographic ID we use an electronic verification system. This system allows us to verify you from basic details using electronic data, however it is not a credit check of any kind so will have no effect on you or your credit history. By signing this agreement as the seller (or purchaser) you understand that we will undertake a search with Credit Referencing Agencies for the purposes of verifying your identity. To do so the Credit Referencing Agencies may check the details you supply against any particulars on any database (public or otherwise) to which they have access. They may also use your details in the future to assist other companies for verification purposes. A record of the search will be retained.",
    ],
  },
];
